import React, { useEffect, useRef, useState } from "react"
import axios from "axios"
import Cookies from "js-cookie"

import {
  Round,
  TeamRound,
  TeamRoundPlayer,
  TeamRoundPlayerScoreMap,
  TeamRoundPlayerHoleInOneFeedPostIdMap,
  AssetMap,
  PlayerHoleScore,
  AlertMessage,
  HoleSkin,
  Hole,
  TeamPlayer,
  roundScreen,
  TimestampedTeamRoundPlayerScoreMap,
  HoleSponsor,
  HoleInOneConfirmation
} from "../types"
import { findTeamRound, getInitialHoleIndex } from "../util"
import calculateScores from "../scoreCalculator"
import { getSharedPusher } from "../../lib/sharedPusher"

import ScoreEntry, { FocusScoreEntry } from "./ScoreEntry"
import Tabs from "./Tabs"
import Alert from "./Alert"
import Nav from "./Nav"
import FAQModal from "./FAQModal"
import HelpModal from "./HelpModal"
import SponsorModal from "./SponsorModal"
import ReviewScores from "./ReviewScores"
import Details from "./Details"
import TeamPlayersScreen from "./TeamPlayersScreen"
import ConfirmCourseScreen from "./ConfirmCourseScreen"
import ConfirmHoleInOne from "./ConfirmHoleInOne"

axios.defaults.timeout = 15000

export interface RoundScreenProps {
  round: Round
  teamId: number
  teamPlayers: TeamPlayer[]
  scores: TimestampedTeamRoundPlayerScoreMap
  backPath: string
  assets: AssetMap
}

export default function RoundScreen(props: RoundScreenProps) {
  const currentUserId: number | null = parseInt(Cookies.get("current_user_id")) || null

  const [data, setData] = useState({
    round: props.round,
    teamId: props.teamId,
    teamPlayers: props.teamPlayers,
    backPath: props.backPath,
    assets: props.assets
  })
  const { round, teamId, teamPlayers, backPath, assets } = data
  const [timestampedScores, setTimestampedScores] = useState(props.scores)
  const { scores, holeInOneFeedPostIds, lastUpdated } = timestampedScores
  const { teamRounds, skins } = calculateScores(round, scores)

  const updateTimestampedScores = (scores) => {
    if (scores.lastUpdated > lastUpdated) {
      setTimestampedScores(scores)
    } else {
      console.log(`Ignored scores with timestamp ${scores.lastUpdated} because latest scores are from ${lastUpdated}`)
    }
  }

  const lastUpdatedRef = useRef(lastUpdated)
  lastUpdatedRef.current = lastUpdated

  useEffect(() => {
    if (!assets.pusherKey) {
      console.warn("pusherKey not set, no pusher updated will be received")
      return
    }
    const pusher = getSharedPusher(assets.pusherKey)
    if (!pusher) return
    const channelName = `round-${round.id}-scores`
    const channel = pusher.subscribe(channelName)
    const onUpdate = (incoming) => {
      if (incoming.lastUpdated > lastUpdatedRef.current) {
        setTimestampedScores(incoming)
      } else {
        console.log(`Ignored scores with timestamp ${incoming.lastUpdated} because latest scores are from ${lastUpdatedRef.current}`)
      }
    }
    channel.bind("update", onUpdate)
    console.log("connected to pusher")
    return () => {
      channel.unbind("update", onUpdate)
      pusher.unsubscribe(channelName)
    }
  }, [])

  let teamRound: TeamRound | null = null
  let playerFinished = false
  let pairing = []
  if (currentUserId) {
    teamRound = findTeamRound(currentUserId, teamId, teamRounds)
    if (teamRound) {
      playerFinished = teamRound.players[0].scores && teamRound.players[0].scores.size >= round.numberOfHoles

      pairing.push(teamRound)
      for (const tr of teamRounds) {
        if (
          tr.id !== teamRound.id &&
          (round.groupStart
            ? tr.startGroup === teamRound.startGroup
            : tr.startTimeOfDay === teamRound.startTimeOfDay &&
              tr.startHoleNumber === teamRound.startHoleNumber &&
              tr.startGroup === teamRound.startGroup)
        ) {
          pairing.push(tr)
        }
      }
    }
  }

  const [holeIndex, setHoleIndex] = useState(
    teamRound ? getInitialHoleIndex(teamRound.startHoleNumber, round.numberOfHoles, teamRound.players[0].id, scores) : 0
  )

  let initialScreen: roundScreen = "details"
  const startsAt = Date.parse(round.startsAt)
  if (!round.cancelationText) {
    if (teamRound?.complete || playerFinished) {
      initialScreen = "review"
    } else if (startsAt <= new Date().valueOf() && teamRound?.players[0]?.scores) {
      initialScreen = "scoring"
    }
  }

  const [screen, setScreen] = useState<roundScreen>(initialScreen)
  const [holeInOneConfirmation, setHoleInOneConfirmation] = useState<HoleInOneConfirmation | null>(null)

  const [alert, setAlert] = useState<AlertMessage | null>(null)

  const [faqVisible, setFaqVisible] = useState(false)

  useEffect(() => {
    if (faqVisible) {
      document.body.classList.add("round-faq-modal--open")
    } else {
      document.body.classList.remove("round-faq-modal--open")
    }
  }, [faqVisible])

  const [helpVisible, setHelpVisible] = useState(false)

  useEffect(() => {
    if (helpVisible) {
      document.body.classList.add("round-help-modal--open")
    } else {
      document.body.classList.remove("round-help-modal--open")
    }
  }, [helpVisible])

  const [visibleSponsor, setVisibleSponsor] = useState<HoleSponsor>(null)

  useEffect(() => {
    if (visibleSponsor) {
      document.body.classList.add("round-sponsor-modal--open")
    } else {
      document.body.classList.remove("round-sponsor-modal--open")
    }
  }, [visibleSponsor])

  let roundProperties = {}
  let roundsItem = window.localStorage?.getItem("rounds")
  if (roundsItem) {
    roundProperties = JSON.parse(roundsItem)
  }

  const [courseConfirmed, setCourseConfirmed] = useState(
    (!round.league.multipleCourses && round.holes.length <= round.numberOfHoles) ||
      roundProperties[round.id]?.courseConfirmed
  )

  const confirmRoundCourse = () => {
    if (roundProperties[round.id]) {
      roundProperties[round.id].courseConfirmed = true
    } else {
      roundProperties[round.id] = { courseConfirmed: true }
    }

    if (window.localStorage) {
      window.localStorage.setItem("rounds", JSON.stringify(roundProperties))
    }
    setCourseConfirmed(true)
  }

  const postScores = async (scores: PlayerHoleScore[]): Promise<any> => {
    try {
      const response = await axios.post("/api/scores", { scores })
      if (response.data && response.data.round) {
        setData({
          round: response.data.round,
          teamId: response.data.teamId,
          teamPlayers: response.data.teamPlayers,
          backPath: response.data.backPath,
          assets: response.data.assets
        })
        updateTimestampedScores(response.data.scores)
        return response.data.scores
      } else {
        console.error("Invalid response from post scores", {
          scores,
          response_status: response.status,
          data: response.data
        })
        // TODO: Not sure what to do if we got here. The scores were probably posted,
        // we just didn't get a valid response from the server so we can't update the state on the client.
        // We don't want to show an error to the user saying scores couldn't be posted, because they were.
      }
    } catch (err) {
      if (err.response && err.response.status === 422 && err.response.data.error) {
        console.error("Could not post scores", { scores, response_status: 422, error: err.response.data.error })
        setAlert({
          title: "Could not save scores",
          message: err.response.data.error,
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.code === "ECONNABORTED") {
        console.error("Could not post scores - network error", { scores })
        setAlert({
          title: "Could not save scores",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.response) {
        console.error("Could not post scores - server error", {
          scores,
          response_status: err.response.status,
          data: err.response.data
        })
        setAlert({
          title: "Could not save scores",
          message: "A server error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.request) {
        console.error("Could not post scores - network error", { scores, response_status: null, error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not save scores",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else {
        console.error("Could not post scores - unknown error", { scores, response_status: null, error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not save scores",
          message: "An error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      }
    }

    return null
  }

  const postRoundComplete = async () => {
    try {
      await axios.post(`/api/team_rounds/${teamRound.id}/complete`)
      window.location.href = `/league_rounds/${round.id}/feedback`
    } catch (err) {
      if (err.response && err.response.status === 422 && err.response.data.error) {
        console.error("Could not complete round", { teamRound, response_status: 422, error: err.response.data.error })
        setAlert({
          title: "Could not save complete round",
          message: err.response.data.error,
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.code === "ECONNABORTED") {
        console.error("Could not complete round - network error", { teamRound })
        setAlert({
          title: "Could not complete round",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.response) {
        console.error("Could not post scores - server error", {
          scores,
          response_status: err.response.status,
          data: err.response.data
        })
        setAlert({
          title: "Could not save scores",
          message: "A server error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.request) {
        console.error("Could not post scores - network error", { scores, response_status: null, error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not save scores",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else {
        console.error("Could not post scores - unknown error", { scores, response_status: null, error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not save scores",
          message: "An error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      }
    }
  }

  const showRoundDetails = () => {
    setScreen("details")
    window.scrollTo(0, 0)
  }

  const enterScores = () => {
    if (courseConfirmed) {
      setScreen("scoring")
    } else {
      setScreen("course")
    }
    window.scrollTo(0, 0)
  }

  const confirmCourse = () => {
    confirmRoundCourse()
    setScreen("scoring")
    window.scrollTo(0, 0)
  }

  const reviewScores = () => {
    setScreen("review")
    window.scrollTo(0, 0)
  }

  const showHoleInOneConfirmation = (confirmation: HoleInOneConfirmation) => {
    setHoleInOneConfirmation(confirmation)
    setScreen("holeInOne")
    window.scrollTo(0, 0)
  }

  const editHoleInOneScores = () => {
    if (holeInOneConfirmation) {
      setHoleIndex(holeInOneConfirmation.holeIndex)
    }
    setHoleInOneConfirmation(null)
    setScreen("scoring")
    window.scrollTo(0, 0)
  }

  const continueAfterHoleInOne = () => {
    const confirmation = holeInOneConfirmation
    setHoleInOneConfirmation(null)
    if (!confirmation) {
      setScreen("scoring")
      return
    }

    if (confirmation.resume.kind === "review") {
      reviewScores()
    } else {
      setHoleIndex(confirmation.resume.index)
      setScreen("scoring")
      window.scrollTo(0, 0)
    }
  }

  const createHoleInOnePost = async (payload: {
    message: string
    attachments: Array<{
      s3Url: string
      fileName: string
      fileSize: number
      mimeType: string
      mediaType: "image"
    }>
    roundId: number
    holeId: number
    teamRoundPlayerIds: number[]
  }): Promise<boolean> => {
    try {
      const response = await axios.post("/api/hole_in_one_posts", payload)
      const feedPostId = response.data?.feedPostId
      const confirmation = holeInOneConfirmation
      if (feedPostId && confirmation) {
        setTimestampedScores((prev) => {
          const nextHoleInOneFeedPostIds: TeamRoundPlayerHoleInOneFeedPostIdMap = {
            ...(prev.holeInOneFeedPostIds || {})
          }
          for (const playerId of payload.teamRoundPlayerIds) {
            const row = [...(nextHoleInOneFeedPostIds[playerId] || [])]
            row[confirmation.holeIndex] = feedPostId
            nextHoleInOneFeedPostIds[playerId] = row
          }
          return { ...prev, holeInOneFeedPostIds: nextHoleInOneFeedPostIds }
        })
      }
      return true
    } catch (err) {
      if (err.response && err.response.status === 422 && err.response.data.error) {
        console.error("Could not create hole in one post", {
          teamRound,
          response_status: 422,
          error: err.response.data.error
        })
        setAlert({
          title: "Could not create hole in one post",
          message: err.response.data.error,
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.code === "ECONNABORTED") {
        console.error("Could not create hole in one post - network error", { teamRound })
        setAlert({
          title: "Could not create hole in one post",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.response) {
        console.error("Could not create hole in one post - server error", {
          response_status: err.response.status,
          data: err.response.data
        })
        setAlert({
          title: "Could not create hole in one post",
          message: "A server error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else if (err.request) {
        console.error("Could not create hole in one post - network error", { error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not create hole in one post",
          message: "A network error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      } else {
        console.error("Could not create hole in one post - unknown error", { error: err })
        window.notifyHoneybadger(err)
        setAlert({
          title: "Could not create hole in one post",
          message: "An error has occurred, please try again",
          buttons: [{ text: "Ok", style: "primary" }]
        })
      }
      return false
    }
  }

  return (
    <React.StrictMode>
      {alert && <Alert alert={alert} clearAlert={() => setAlert(null)} />}
      {faqVisible ? <FAQModal round={round} assets={assets} dismiss={() => setFaqVisible(false)} /> : <></>}
      {helpVisible ? <HelpModal round={round} assets={assets} dismiss={() => setHelpVisible(false)} /> : <></>}
      {visibleSponsor ? (
        <SponsorModal sponsor={visibleSponsor} assets={assets} dismiss={() => setVisibleSponsor(null)} />
      ) : (
        <></>
      )}
      <Nav
        modal={!(!teamRound || teamRound.complete || screen === "details")}
        backPath={backPath}
        assets={assets}
        showRoundDetails={showRoundDetails}
        shouldShowPlayers={teamRound && !teamRound.complete}
        showPlayers={() => setScreen("players")}
        showFAQ={() => setFaqVisible(true)}
        showHelp={() => setHelpVisible(true)}
      />
      <div className="app-screen-container" style={{ paddingBottom: "1000px" }}>
        <Content
          screen={screen}
          round={round}
          currentUserId={currentUserId}
          holeIndex={holeIndex}
          setHoleIndex={setHoleIndex}
          teamPlayers={teamPlayers}
          teamRound={teamRound}
          assets={assets}
          scores={scores}
          holeInOneFeedPostIds={holeInOneFeedPostIds}
          postScores={postScores}
          teamId={teamId}
          teamRounds={teamRounds}
          pairing={pairing}
          skins={skins}
          postRoundComplete={postRoundComplete}
          enterScores={enterScores}
          confirmCourse={confirmCourse}
          reviewScores={reviewScores}
          holeInOneConfirmation={holeInOneConfirmation}
          showHoleInOneConfirmation={showHoleInOneConfirmation}
          editHoleInOneScores={editHoleInOneScores}
          continueAfterHoleInOne={continueAfterHoleInOne}
          createHoleInOnePost={createHoleInOnePost}
          showFAQ={() => setFaqVisible(true)}
          showHelp={() => setHelpVisible(true)}
          showSponsor={(s) => setVisibleSponsor(s)}
          setAlert={setAlert}
        />
      </div>
    </React.StrictMode>
  )
}

interface ContentProps {
  screen: string
  round: Round
  currentUserId: number
  holeIndex: number
  setHoleIndex: (index: number) => void
  teamId: number
  teamPlayers: TeamPlayer[]
  teamRound: TeamRound
  assets: AssetMap
  scores: TeamRoundPlayerScoreMap
  holeInOneFeedPostIds?: TeamRoundPlayerHoleInOneFeedPostIdMap
  postScores: (scores: PlayerHoleScore[]) => Promise<boolean>
  teamRounds: TeamRound[]
  pairing: TeamRound[]
  skins: Map<Hole, HoleSkin>
  postRoundComplete()
  enterScores()
  confirmCourse()
  reviewScores()
  holeInOneConfirmation: HoleInOneConfirmation | null
  showHoleInOneConfirmation: (confirmation: HoleInOneConfirmation) => void
  editHoleInOneScores: () => void
  continueAfterHoleInOne: () => void
  createHoleInOnePost: (payload: {
    message: string
    attachments: Array<{
      s3Url: string
      fileName: string
      fileSize: number
      mimeType: string
      mediaType: "image"
    }>
    roundId: number
    holeId: number
    teamRoundPlayerIds: number[]
  }) => Promise<boolean>
  showFAQ()
  showHelp()
  showSponsor: (sponsor: HoleSponsor) => void
  setAlert: (message: AlertMessage) => void
}

function Content({
  screen,
  round,
  currentUserId,
  holeIndex,
  setHoleIndex,
  teamPlayers,
  teamRound,
  assets,
  scores,
  holeInOneFeedPostIds,
  postScores,
  teamRounds,
  pairing,
  skins,
  postRoundComplete,
  enterScores,
  confirmCourse,
  reviewScores,
  holeInOneConfirmation,
  showHoleInOneConfirmation,
  editHoleInOneScores,
  continueAfterHoleInOne,
  createHoleInOnePost,
  showFAQ,
  showHelp,
  showSponsor,
  setAlert
}: ContentProps) {
  const focusScoreEntryRef = useRef<FocusScoreEntry | null>(null)
  const onScoreCellPress = (index: number, focusPlayer: TeamRoundPlayer) => {
    focusScoreEntryRef.current?.(index, focusPlayer)
  }

  if (!teamRound || teamRound.complete || screen === "details") {
    return (
      <>
        <Details
          round={round}
          teamRound={teamRound}
          teamPlayers={teamPlayers}
          currentUserId={currentUserId}
          assets={assets}
          showFAQ={showFAQ}
          enterScores={enterScores}
          showAlert={setAlert}
        />
        {round.cancelationText ? (
          <></>
        ) : (
          <Tabs
            holeIndex={holeIndex}
            round={round}
            teamRounds={teamRounds}
            teamRound={teamRound}
            currentUserId={currentUserId}
            pairing={pairing}
            skins={skins}
            assets={assets}
          />
        )}
      </>
    )
  }

  if (screen === "players") {
    return <TeamPlayersScreen currentUserId={currentUserId} round={round} teamRound={teamRound} />
  }

  if (screen === "course") {
    return <ConfirmCourseScreen round={round} teamRound={teamRound} confirmCourse={confirmCourse} showHelp={showHelp} />
  }

  if (screen === "review") {
    return (
      <ReviewScores
        round={round}
        pairing={pairing}
        assets={assets}
        postRoundComplete={postRoundComplete}
        enterScores={enterScores}
        showAlert={setAlert}
      />
    )
  }

  // Keep ScoreEntry mounted under the hole-in-one screen so local score state
  // (and Save Scores) still works after Edit Scores / a failed photo upload.
  if (screen === "scoring" || screen === "holeInOne") {
    return (
      <>
        {screen === "holeInOne" && holeInOneConfirmation ? (
          <ConfirmHoleInOne
            confirmation={holeInOneConfirmation}
            roundId={round.id}
            currentUserId={currentUserId}
            editScores={editHoleInOneScores}
            skip={continueAfterHoleInOne}
            createHoleInOnePost={createHoleInOnePost}
            assets={assets}
          />
        ) : null}
        <div className="screen-body" style={{ display: screen === "holeInOne" ? "none" : undefined }}>
          <ScoreEntry
            holeIndex={holeIndex}
            setHoleIndex={setHoleIndex}
            player={teamRound?.players && teamRound?.players[0]}
            round={round}
            teamRound={teamRound}
            assets={assets}
            scores={scores}
            holeInOneFeedPostIds={holeInOneFeedPostIds}
            postScores={postScores}
            reviewScores={reviewScores}
            showHoleInOneConfirmation={showHoleInOneConfirmation}
            showAlert={setAlert}
            showSponsor={showSponsor}
            focusScoreEntryRef={focusScoreEntryRef}
          />
          <Tabs
            holeIndex={holeIndex}
            round={round}
            teamRounds={teamRounds}
            teamRound={teamRound}
            currentUserId={currentUserId}
            pairing={pairing}
            skins={skins}
            assets={assets}
            editableTeamRoundId={teamRound.id}
            onScoreCellPress={onScoreCellPress}
          />
        </div>
      </>
    )
  }

  return null
}
